import { isDefined } from '@sgme/fp';

import type { TcHedgeType } from '@/api/option/requestOptionRfs.ts';
import { toMultipassLegId, toRfsDate } from '@/api/option/utils.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import type { FxOption, MultipassLegId } from '@/store/option/state.ts';
import type { CurrencyChoice, PriceType } from '@/types/common.ts';

interface OptionForexOptionLeg {
  id: MultipassLegId;
  premiumBidValue?: number;
  premiumAskValue?: number;
  markupBidValue?: number;
  markupAskValue?: number;
  volatilityBidValue?: number;
  volatilityAskValue?: number;
}

interface OptionForexOptionProduct {
  markupCurrency: CurrencyChoice;
  displayPriceCurrency: CurrencyChoice;
  displayPriceType: PriceType;
  amountReference?: number;
  amountReferenceCurrency?: string;
  legs: OptionForexOptionLeg[];
  hedges: IFxoHedgeUpdate[];
}

/**
 * TODO: check with backend why this is different from request
 * Update: https://sgithub.fr.world.socgen/SGMARKETS-EXECUTION/fx/blob/af6a37600cbbc5e92fd13d73073190b64cd877d9/share/SgMarkets.Execution.Fx.Share/Models/Multipass/ForexOptionHedge.cs#L30
 * It's using the MP DTO without mapping
 */
export interface IFxoHedgeUpdate {
  hedgeType: TcHedgeType;
  maturityDate?: Date;
  price?: string;
  notionalAmount?: string;
  notionalCurrency?: string;
}

interface OptionQuoteUpdatePayload {
  rfsId: string;
  version: number;
  forexOptionProduct: OptionForexOptionProduct;
}

export interface UpdateOptionParams {
  tileId: string;
  rfsId: string;
  request: OptionQuoteUpdatePayload;
}

export const updateOptionRfs = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<unknown, UpdateOptionParams>({
    query: (params) => {
      return {
        url: '/fxOption/rfs/update',
        method: 'POST',
        body: params.request,
      };
    },
  });
};

export function createUpdateRfsParams(option: FxOption, tileId: string, rfsId: string): UpdateOptionParams {
  const tileProperties = option.properties;
  const optionCurrency = selectOptionCurrency(option);
  const { currency1, currency2 } = optionCurrency;

  const premiumCategory = tileProperties.premiumType.premiumCategory;
  const premiumCurrencyIndex = tileProperties.premiumType.premiumCurrency;
  const markupCurrency: CurrencyChoice = tileProperties.markupCurrency === currency2 ? 2 : 1;
  const premiumCurrencyLabel = premiumCurrencyIndex === 2 ? currency2 : currency1;

  const hedgeType = tileProperties.hedgeType;
  const hedges = tileProperties.hedges;

  const legsProperties = tileProperties.legs;
  // TODO: this is a temporary fix because our backend assumes both fields are set but we need to see with pricer
  const solving = legsProperties?.some((leg) => isDefined(leg.premiumAsk) || isDefined(leg.premiumBid));

  const product: OptionForexOptionProduct = {
    markupCurrency: markupCurrency,
    displayPriceType: premiumCategory,
    displayPriceCurrency: premiumCurrencyIndex,
    amountReference: tileProperties.amountReference,
    amountReferenceCurrency: isDefined(tileProperties.amountReference) ? premiumCurrencyLabel : undefined,
    legs: legsProperties.map((leg) => ({
      id: toMultipassLegId(leg.legId, option.type),
      premiumAskValue: solving ? (leg.premiumAsk ?? 0) : undefined,
      premiumBidValue: solving ? (leg.premiumBid ?? 0) : undefined,
      markupBidValue: leg.markup,
      markupAskValue: leg.markup,
      volatilityBidValue: leg.volatilityBid,
      volatilityAskValue: leg.volatilityAsk,
    })),
    hedges:
      hedgeType === 'Live'
        ? []
        : hedges.map((hedge) => ({
            hedgeType,
            maturityDate: toRfsDate(hedge.maturityDate),
            price: hedge.rate?.toString(),
            notionalAmount: hedge.notionalAmount?.toString(),
            notionalCurrency: hedge.notionalCurrency === 2 ? currency2 : currency1,
          })),
  };

  return {
    tileId,
    rfsId,
    request: {
      rfsId,
      version: (tileProperties.rfsVersion ?? 0) + 1,
      forexOptionProduct: product,
    },
  };
}
